<template>
  <div class="col-span-3 lg:col-span-1 gap-y-4">
    <h3>Filter by</h3>
    <aside class="filters">
      <!-- Keyword Search -->
      <div class="facet-link-box">
        <div class="form-group w-full">
          <h4>
            <label for="filter-keyword">Keyword</label>
          </h4>
          <input
            id="filter-keyword"
            v-model="localFilters.keyword"
            type="text"
            class="form-control fuzzy-wuzzy w-full"
            placeholder="Filter"
            aria-label="Filter certificate holders by keyword"
          />
          <button
            v-if="localFilters.keyword"
            @click="localFilters.keyword = ''"
            class="block cursor-pointer clear-button relative left-[calc(100%-30px)] -translate-y-8 size-0"
            aria-label="Clear keyword filter"
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="1.4em" height="1.4em" viewBox="0 0 24 24">
              <path fill="currentColor" d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"/>
            </svg>
          </button>
        </div>
      </div>
      <!-- Last Name Filter -->
      <div class="facet-link-box">
        <h4 class="select-none">Last Name</h4>
        <ul class="filter list-none pl-0 flex flex-row flex-wrap text-red-600 gap-2" role="list">
          <li
            v-for="letter in alphaLetters"
            :key="letter"
            class="w-8 h-8 leading-none inline-flex flex-row justify-center group gap-2 hover:bg-gray-200"
          >
            <label class="form-check-label w-8 h-8 p-2 text-center cursor-pointer">
              <input
                class="form-check-input filterCheckbox hidden"
                type="radio"
                name="lastNameFilter"
                :value="letter"
                v-model="localFilters.lastName"
                hidden
                :aria-checked="localFilters.lastName === letter"
                :aria-label="`Filter by last name starting with ${letter}`"
              />
              <span
                class="text-black/80 text-center mx-auto group-hover:text-red-600 group-active:text-red-600 group-focus:text-red-600"
                :class="{ 'font-bold': localFilters.lastName === letter }"
                tabindex="0"
                role="button"
                @keydown.enter="localFilters.lastName = letter"
                @keydown.space.prevent="localFilters.lastName = letter"
              >{{ letter }}</span>
            </label>
          </li>
          <li>
            <button
              v-if="localFilters.lastName"
              @click="localFilters.lastName = ''"
              class="ml-2 text-xs text-gray-500 underline"
              type="button"
              aria-label="Clear last name filter"
            >Clear</button>
          </li>
        </ul>
      </div>
      <!-- Certificate Type Filter -->
      <div class="facet-link-box">
        <h4>Type</h4>
        <ul class="filter">
          <li
            v-for="(type, idx) in certificateTypes"
            :key="type"
            :class="idx > 4 && !showAllTypes ? 'tFilter filterOverflow' : 'tFilter'"
          >
            <div class="form-check group">
              <label class="form-check-label text-left">
                <input
                  class="form-check-input filterCheckbox"
                  type="checkbox"
                  :value="type"
                  v-model="localFilters.certificateTypes"
                  :aria-checked="localFilters.certificateTypes.includes(type)"
                  :aria-label="`Filter by certificate type ${type}`"
                />
                <span class="text-black/80 mr-auto select-none cursor-pointer text-left pl-2 group-hover:text-red-600 group-active:text-red-600 group-focus:text-red-600">{{ type }}</span>
              </label>
            </div>
          </li>
          <div id="topicsToggleFilter" class="expandFilter">
            <span v-if="showAllTypes" class="less" @click="$emit('toggle-types', false)" tabindex="0" role="button">less</span>
            <span v-else class="more" @click="$emit('toggle-types', true)" tabindex="0" role="button">more</span>
            <svg xmlns="http://www.w3.org/2000/svg" class="inline-block" width="32" height="32" viewBox="0 0 24 24">
              <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m19 15l-7-6l-7 6"/>
            </svg>
          </div>
        </ul>
      </div>
      <div class="mt-2 flex flex-col items-start">
        <!-- Alpha filter options here -->
        <button
          v-if="hasActiveAlpha"
          @click="$emit('clear-alpha')"
          class="mt-2 text-xs text-gray-500 underline"
          type="button"
          aria-label="Clear alpha filter"
        >Clear</button>
      </div>
    </aside>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, toRefs, computed } from 'vue'

const props = defineProps<{
  filters: {
    keyword: string
    lastName: string
    certificateTypes: string[]
  }
  certificateTypes: string[]
  alphaLetters: string[]
  showAllTypes: boolean
}>()

const emit = defineEmits(['update:filters', 'toggle-types'])

const localFilters = ref({ ...props.filters })

watch(
  () => props.filters,
  (newVal) => {
    localFilters.value = { ...newVal }
  },
  { deep: true }
)

watch(
  localFilters,
  (val) => {
    emit('update:filters', val)
  },
  { deep: true }
)

const hasActiveAlpha = computed(() => !!props.filters.lastName)
</script>
